/**
 * Gameplay sync utilities for storing/loading scene gameplay data
 * (version 2 / remote-asset format) in Firestore.
 *
 * Scenes are stored under the user document's `orchestratorSavedData` map:
 *   users/{username}  →  { orchestratorSavedData: { scenes: { [sceneId]: v2Payload, … }, ... }, … }
 *
 * This keeps data co-located with the user object (access, email, etc.)
 * and allows individual scene updates via dot-notation field paths without
 * any read-modify-write cycle or subcollection fan-out.
 *
 * IMPORTANT: Firestore FieldValue sentinels (serverTimestamp, etc.) must only
 * be used as direct leaf values in updateDoc/setDoc calls.  They CANNOT be
 * nested inside a plain-object map value (e.g. as a field inside the per-scene
 * object assigned to `scenes.${id}`).  All timestamps inside scene payloads
 * therefore use ISO-8601 strings produced by new Date().toISOString(), while
 * serverTimestamp() is reserved for the top-level `syncUpdatedAt` field only.
 *
 * IMPORTANT: All scene payload objects are passed through sanitizeForFirestore()
 * before writing.  This strips `undefined` values (Firestore rejects documents
 * that contain undefined) by performing a JSON round-trip.
 */
import {
  deleteField,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { getAssetUrl } from './assetUpload';

// ── localStorage key ───────────────────────────────────────────────────────
const GAMEPLAY_SYNC_KEY = 'gameplaySyncEnabled';

// ── Firestore collection constant ──────────────────────────────────────────
const USERS_COLLECTION = 'users';

// ── Firestore serialisation helper ────────────────────────────────────────

/**
 * Firestore rejects documents that contain `undefined` values anywhere in
 * the payload.  A JSON round-trip drops undefined (JSON.stringify omits
 * undefined-valued keys) so we end up with a clean, serialisable object.
 */
function sanitizeForFirestore(obj) {
  return JSON.parse(JSON.stringify(obj));
}

// ── localStorage helpers ───────────────────────────────────────────────────

export function getGameplaySyncEnabled() {
  try {
    return localStorage.getItem(GAMEPLAY_SYNC_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setGameplaySyncEnabledLocal(enabled) {
  try {
    if (enabled) {
      localStorage.setItem(GAMEPLAY_SYNC_KEY, 'true');
    } else {
      localStorage.removeItem(GAMEPLAY_SYNC_KEY);
    }
  } catch {
    // ignore
  }
}

// ── Payload builder ────────────────────────────────────────────────────────

/**
 * Build a v2 gameplay-only payload for a scene.
 * Images are referenced by their remote URL (from the asset URL map).
 * Images that have not been uploaded yet are included without a URL.
 */
export function buildGameplayPayload(scene) {
  const layers = (scene.layers || []).map((layer) => {
    const url = getAssetUrl(layer.imageId);
    const { imageId: _lid, ...rest } = layer;
    return url ? { ...rest, imageUrl: url } : rest;
  });

  const tokens = (scene.tokens || []).map((token) => {
    const goodUrl = getAssetUrl(token.goodImageId || token.imageId);
    const evilUrl = getAssetUrl(token.evilImageId || token.altImageId);
    const neutralUrl = getAssetUrl(token.neutralImageId);
    const {
      // eslint-disable-next-line no-unused-vars
      imageId: _tid,
      altImageId: _aid,
      goodImageId: _gid,
      evilImageId: _eid,
      neutralImageId: _nid,
      ...rest
    } = token;
    return {
      ...rest,
      ...(goodUrl ? { goodImageUrl: goodUrl } : {}),
      ...(evilUrl ? { evilImageUrl: evilUrl } : {}),
      ...(neutralUrl ? { neutralImageUrl: neutralUrl } : {}),
    };
  });

  const battleEffects = (scene.battleEffects || []).map((effect) => ({
    ...effect,
    cells: Array.isArray(effect.cells)
      ? effect.cells.map((cell) => ({
          x: Number.isFinite(cell.x) ? Number(cell.x) : 0,
          y: Number.isFinite(cell.y) ? Number(cell.y) : 0,
        }))
      : [],
  }));

  return {
    version: 2,
    assetMode: 'remote',
    exportedAt: new Date().toISOString(),
    id: scene.id,
    name: scene.name || '',
    effect: scene.effect || null,
    ttrpgSystem: scene.ttrpgSystem || null,
    layers,
    tokens,
    battleEffects,
  };
}

// ── Firestore ref helper ───────────────────────────────────────────────────

function getUserRef(db, username) {
  return doc(db, USERS_COLLECTION, username);
}

// ── Firestore operations ───────────────────────────────────────────────────

/**
 * Persist one scene's v2 gameplay payload into the user document's
 * `scenes` map field.  Uses a dot-notation field path so only this
 * single scene entry is written — no read required.
 *
 * NOTE: serverTimestamp() must not be nested inside a plain-object map
 * value in updateDoc.  The per-scene `updatedAt` is therefore a plain
 * ISO-8601 string; only the root `syncUpdatedAt` uses serverTimestamp().
 */
export async function saveSceneGameplay(db, username, scene) {
  const payload = sanitizeForFirestore({
    ...buildGameplayPayload(scene),
    updatedAt: new Date().toISOString(),
  });
  const ref = getUserRef(db, username);
  await updateDoc(ref, {
    [`orchestratorSavedData.scenes.${scene.id}`]: payload,
    'orchestratorSavedData.syncUpdatedAt': serverTimestamp(),
  });
}

/**
 * Persist all scenes' v2 payloads into the user document in one write.
 * Replaces the entire `scenes` map (used when enabling sync or bulk-restore).
 *
 * ISO-8601 strings are used for per-scene timestamps; serverTimestamp() is
 * used only for the top-level `syncUpdatedAt` sentinel.
 */
export async function saveAllScenesGameplay(db, username, scenes) {
  if (!Array.isArray(scenes)) return;
  const now = new Date().toISOString();
  const scenesMap = {};
  for (const scene of scenes) {
    if (!scene || !scene.id) continue;
    scenesMap[scene.id] = sanitizeForFirestore({
      ...buildGameplayPayload(scene),
      updatedAt: now,
    });
  }
  const ref = getUserRef(db, username);
  await updateDoc(ref, {
    'orchestratorSavedData.scenes': scenesMap,
    'orchestratorSavedData.syncUpdatedAt': serverTimestamp(),
  });
}

/**
 * Remove gameplay sync payload fields from the user document.
 * Used when gameplay sync is disabled.
 */
export async function clearAllScenesGameplay(db, username) {
  const ref = getUserRef(db, username);
  await updateDoc(ref, {
    'orchestratorSavedData.scenes': deleteField(),
    'orchestratorSavedData.syncUpdatedAt': deleteField(),
  });
}

/**
 * Remove a single scene from the user document's `scenes` map.
 */
export async function deleteSceneGameplay(db, username, sceneId) {
  const ref = getUserRef(db, username);
  await updateDoc(ref, {
    [`orchestratorSavedData.scenes.${sceneId}`]: deleteField(),
    'orchestratorSavedData.syncUpdatedAt': serverTimestamp(),
  });
}

/**
 * Load all synced scene gameplay payloads for a user.
 * Returns an array of v2 payload objects from the user document's `scenes` map.
 */
export async function loadScenesGameplay(db, username) {
  const ref = getUserRef(db, username);
  const snap = await getDoc(ref);
  if (!snap.exists()) return [];
  const data = snap.data() || {};
  const scenesMap = data.orchestratorSavedData?.scenes || {};
  return Object.values(scenesMap).filter(Boolean);
}
